import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuthStore } from '../../stores/authStore';
import {
  subscribeToConversations,
  subscribeToThread,
  sendAdminReply,
  markAdminRead,
  resolveConversation,
} from '../../lib/adminMessaging';
import type { Conversation, ThreadMessage } from '../../lib/adminMessaging';

function timeAgo(ts: { toDate: () => Date } | null): string {
  if (!ts) return '';
  const diff = Date.now() - ts.toDate().getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export default function AdminInbox() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);

  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [thread, setThread] = useState<ThreadMessage[]>([]);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  // Check admin access
  useEffect(() => {
    if (!user) { setIsAdmin(false); return; }
    getDoc(doc(db, 'adminUsers', user.uid))
      .then((snap) => setIsAdmin(snap.exists()))
      .catch(() => setIsAdmin(false));
  }, [user]);

  // Subscribe to all conversations
  useEffect(() => {
    if (!isAdmin) return;
    return subscribeToConversations(setConversations);
  }, [isAdmin]);

  // Subscribe to selected thread
  useEffect(() => {
    if (!selected) return;
    markAdminRead(selected).catch(() => {});
    return subscribeToThread(selected, (msgs) => {
      setThread(msgs);
      setTimeout(
        () => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }),
        50,
      );
    });
  }, [selected]);

  async function handleReply() {
    const text = reply.trim();
    if (!text || !selected || sending) return;
    setSending(true);
    try {
      await sendAdminReply(selected, text, 'KoKo');
      setReply('');
    } finally {
      setSending(false);
    }
  }

  const selectedConv = conversations.find((c) => c.id === selected);

  // ── Loading ──────────────────────────────────────────────────────────────────
  if (isAdmin === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-base">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-white/10 border-t-amber" />
      </div>
    );
  }

  // ── Not admin ────────────────────────────────────────────────────────────────
  if (!isAdmin) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-base text-ink">
        <p className="font-marker text-2xl text-coral">Access denied</p>
        <button
          onClick={() => navigate('/')}
          className="rounded-2xl border border-white/10 bg-white/5 px-5 py-2.5 font-gill text-sm text-ink/70 hover:bg-white/10"
        >
          Go home
        </button>
      </div>
    );
  }

  const totalUnread = conversations.reduce((n, c) => n + (c.unreadByAdmin ?? 0), 0);

  return (
    <div className="flex h-screen flex-col bg-base text-ink" style={{ background: '#0D0F1C' }}>
      {/* Top bar */}
      <header className="flex h-14 shrink-0 items-center justify-between border-b border-white/10 px-5">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="flex h-8 w-8 items-center justify-center rounded-full border border-white/10 bg-white/5 text-ink/60 hover:bg-white/10"
          >
            ←
          </button>
          <h1 className="font-marker text-xl text-amber">
            KoKo Inbox
            {totalUnread > 0 && (
              <span className="ml-2 rounded-full bg-coral px-2 py-0.5 font-marker text-xs text-white">
                {totalUnread}
              </span>
            )}
          </h1>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* Conversation list */}
        <aside className="flex w-72 shrink-0 flex-col border-r border-white/10 overflow-y-auto">
          {conversations.length === 0 && (
            <div className="flex flex-1 flex-col items-center justify-center gap-2 py-16 text-center">
              <span className="text-4xl">💬</span>
              <p className="font-gill text-sm text-ink/50">No messages yet</p>
            </div>
          )}
          {conversations.map((conv) => (
            <button
              key={conv.id}
              onClick={() => setSelected(conv.id)}
              className={`flex flex-col gap-1 border-b border-white/5 px-4 py-3 text-left transition-colors hover:bg-white/5 ${
                selected === conv.id ? 'bg-amber/5' : ''
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="font-marker text-sm text-ink truncate">
                  {conv.explorerName || 'Explorer'}
                </span>
                <div className="flex items-center gap-1.5 shrink-0">
                  {conv.unreadByAdmin > 0 && (
                    <span className="rounded-full bg-amber px-1.5 py-0.5 font-marker text-[10px] text-base">
                      {conv.unreadByAdmin}
                    </span>
                  )}
                  {conv.status === 'resolved' && (
                    <span className="rounded-full border border-mint/30 px-1.5 py-0.5 font-marker text-[10px] text-mint">
                      done
                    </span>
                  )}
                </div>
              </div>
              <p className="truncate font-gill text-xs text-ink/50">{conv.lastMessage}</p>
              <p className="font-gill text-[10px] text-ink/30">{timeAgo(conv.lastMessageAt)}</p>
            </button>
          ))}
        </aside>

        {/* Thread */}
        {selected ? (
          <div className="flex flex-1 flex-col overflow-hidden">
            {/* Thread header */}
            <div className="flex shrink-0 items-center justify-between border-b border-white/10 px-5 py-3">
              <div>
                <p className="font-marker text-base text-ink">
                  {selectedConv?.explorerName || 'Explorer'}
                </p>
                <p className="font-gill text-xs text-ink/40">
                  {selectedConv?.status === 'resolved' ? 'Resolved' : 'Open'}
                </p>
              </div>
              {selectedConv?.status === 'open' && (
                <button
                  onClick={() => resolveConversation(selected)}
                  className="rounded-2xl border border-mint/30 bg-mint/10 px-3 py-1.5 font-marker text-xs text-mint transition-colors hover:bg-mint/20"
                >
                  Mark resolved
                </button>
              )}
            </div>

            {/* Messages */}
            <div className="flex-1 space-y-3 overflow-y-auto px-5 py-4">
              {thread.map((msg) => (
                <div
                  key={msg.id}
                  className={`flex ${msg.fromAdmin ? 'justify-end' : 'justify-start'}`}
                >
                  <div className="max-w-[70%]">
                    <p className={`mb-0.5 font-gill text-[10px] ${msg.fromAdmin ? 'text-right text-amber/60' : 'text-ink/40'}`}>
                      {msg.fromAdmin ? 'You (KoKo)' : msg.senderName}
                    </p>
                    <div
                      className="rounded-2xl px-4 py-2.5 font-gill text-sm leading-relaxed"
                      style={
                        msg.fromAdmin
                          ? { background: '#F59E0B', color: '#000' }
                          : { background: 'rgba(255,255,255,0.07)', color: 'rgba(232,234,240,0.9)' }
                      }
                    >
                      {msg.content}
                    </div>
                    {msg.createdAt && (
                      <p className={`mt-0.5 font-gill text-[10px] text-ink/25 ${msg.fromAdmin ? 'text-right' : ''}`}>
                        {timeAgo(msg.createdAt)}
                      </p>
                    )}
                  </div>
                </div>
              ))}
              <div ref={bottomRef} />
            </div>

            {/* Reply input */}
            <div className="shrink-0 border-t border-white/10 px-5 py-3">
              <div className="flex items-end gap-2">
                <textarea
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleReply();
                    }
                  }}
                  disabled={sending}
                  placeholder="Reply as KoKo…"
                  rows={2}
                  className="max-h-32 flex-1 resize-none rounded-2xl border border-white/10 bg-white/5 px-4 py-2.5 font-gill text-sm text-ink placeholder:text-ink/30 focus:border-amber/40 focus:outline-none disabled:opacity-40"
                />
                <button
                  onClick={handleReply}
                  disabled={sending || !reply.trim()}
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition-all disabled:opacity-30"
                  style={{ background: '#F59E0B' }}
                  aria-label="Send reply"
                >
                  <svg className="h-4 w-4 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 text-center">
            <span className="text-5xl">🦊</span>
            <p className="font-marker text-lg text-ink/50">Select a conversation</p>
          </div>
        )}
      </div>
    </div>
  );
}
